import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { FILTER_IDS, HORIZONTAL_CHIP_ORDER } from "../FilterBar/filterIds";
import { formatResultsCountLabel } from "../FilterBar/formatResultsCount";
import ResultViewToggle from "../FilterBar/ResultViewToggle";
import { useFilterControllerContext } from "../FilterBar/FilterControllerContext";
import useFilterController from "../FilterBar/useFilterController";
import AppliedFiltersRow from "./AppliedFiltersRow";
import MobileFilterSheet from "./MobileFilterSheet";
import {
  MOBILE_COMPACT_GAP,
  MOBILE_TOUCH_MIN_PX,
  MOBILE_TYPE,
} from "../../../theme/mobileLayout";

export function MobileSearchFilterBarView({
  controller,
  resultView,
  onResultViewChange,
  totalRows = 0,
  showResultsCount = true,
  onExport,
  isExporting = false,
  exportDisabled = false,
  exportTitle,
  onSaveSearch,
  saveSearchDisabled = false,
  saveSearchLabel = "Save search",
  showNaturalLanguageFilter = false,
}) {
  const {
    summaries,
    showClearFilters,
    clearFilters,
    filtersDisabled,
  } = controller;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [focusFilterId, setFocusFilterId] = useState(null);

  const filterOrder = useMemo(() => showNaturalLanguageFilter
    && !HORIZONTAL_CHIP_ORDER.includes(FILTER_IDS.NATURAL_LANGUAGE)
    ? [...HORIZONTAL_CHIP_ORDER, FILTER_IDS.NATURAL_LANGUAGE]
    : HORIZONTAL_CHIP_ORDER, [showNaturalLanguageFilter]);
  const activeCount = useMemo(
    () => filterOrder.filter((id) => summaries[id]?.isActive).length,
    [filterOrder, summaries]
  );
  const resultsCountLabel = useMemo(() => formatResultsCountLabel(totalRows), [totalRows]);

  const openSheet = (filterId = null) => {
    setFocusFilterId(filterId);
    setSheetOpen(true);
  };

  return (
    <Box data-testid="mobile-search-filter-bar" sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: MOBILE_COMPACT_GAP,
          flexWrap: "wrap",
          overflow: "visible",
          bgcolor: "background.paper",
          borderRadius: 2,
          px: 1,
          py: 0.75,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 1px 3px rgba(0, 0, 0, 0.35)"
              : "0 1px 3px rgba(88, 89, 91, 0.1)",
        }}
      >
        {/* Filters → count → view toggle → download → save */}
        <Badge
          badgeContent={activeCount || null}
          color="primary"
          overlap="circular"
          sx={{ flexShrink: 0, "& .MuiBadge-badge": { fontSize: 10, fontWeight: 700 } }}
        >
          <Button
            type="button"
            variant="contained"
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            onClick={() => openSheet(null)}
            disabled={filtersDisabled}
            sx={{
              ...MOBILE_TYPE.button,
              minHeight: MOBILE_TOUCH_MIN_PX,
              borderRadius: 2,
              px: 1.25,
              textTransform: "none",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { boxShadow: "none" },
              "& .MuiButton-startIcon": { mr: 0.5 },
            }}
          >
            Filters
          </Button>
        </Badge>

        {/* Secondary: count + view toggle */}
        {showResultsCount ? (
          <Typography
            sx={{
              ...MOBILE_TYPE.helper,
              color: "text.secondary",
              flex: "1 1 auto",
              minWidth: 0,
              textAlign: "left",
              px: 0.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            data-testid="mobile-results-counter"
          >
            {resultsCountLabel}
          </Typography>
        ) : null}

        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            justifyContent: "center",
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          <ResultViewToggle value={resultView} onChange={onResultViewChange} />
        </Box>

        {/* Tertiary: export / save — icon-only, quieter */}
        <Tooltip title={exportTitle || "Download results (.csv)"}>
          <span>
            <IconButton
              aria-label={exportTitle || "Export CSV"}
              onClick={onExport}
              disabled={exportDisabled || isExporting}
              size="small"
              sx={{
                flexShrink: 0,
                minWidth: MOBILE_TOUCH_MIN_PX,
                minHeight: MOBILE_TOUCH_MIN_PX,
                color: "text.secondary",
                "& .MuiSvgIcon-root": { fontSize: 20 },
              }}
            >
              {isExporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          aria-label={saveSearchLabel}
          onClick={onSaveSearch}
          disabled={saveSearchDisabled}
          size="small"
          sx={{
            flexShrink: 0,
            minWidth: MOBILE_TOUCH_MIN_PX,
            minHeight: MOBILE_TOUCH_MIN_PX,
            color: "text.secondary",
            "& .MuiSvgIcon-root": { fontSize: 20 },
          }}
        >
          <SaveOutlinedIcon />
        </IconButton>
      </Box>

      <Box sx={{ mt: 0.75, mx: -0.25 }}>
        <AppliedFiltersRow
          summaries={summaries}
          showClear={showClearFilters}
          onClear={clearFilters}
          onChipClick={(id) => openSheet(id)}
        />
      </Box>

      <MobileFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        controller={controller}
        initialFilterId={focusFilterId}
        showNaturalLanguageFilter={showNaturalLanguageFilter}
      />
    </Box>
  );
}

function MobileSearchFilterBarStandalone(props) {
  const {
    resultView,
    onResultViewChange,
    totalRows,
    showResultsCount,
    onExport,
    isExporting,
    exportDisabled,
    exportTitle,
    onSaveSearch,
    saveSearchDisabled,
    saveSearchLabel,
    showNaturalLanguageFilter,
    isNaturalLanguageMode,
    ...filterProps
  } = props;

  const controller = useFilterController({
    ...filterProps,
    isNaturalLanguageMode,
  });

  return (
    <MobileSearchFilterBarView
      controller={controller}
      resultView={resultView}
      onResultViewChange={onResultViewChange}
      totalRows={totalRows}
      showResultsCount={showResultsCount}
      onExport={onExport}
      isExporting={isExporting}
      exportDisabled={exportDisabled}
      exportTitle={exportTitle}
      onSaveSearch={onSaveSearch}
      saveSearchDisabled={saveSearchDisabled}
      saveSearchLabel={saveSearchLabel}
      showNaturalLanguageFilter={showNaturalLanguageFilter}
    />
  );
}

/**
 * Mobile replacement for the horizontal chip filter bar.
 * Hierarchy: Filters (primary) → count + view (secondary) → export/save (tertiary).
 */
export default function MobileSearchFilterBar(props) {
  const shared = useFilterControllerContext();
  const {
    resultView,
    onResultViewChange,
    totalRows,
    showResultsCount = true,
    onExport,
    isExporting,
    exportDisabled,
    exportTitle,
    onSaveSearch,
    saveSearchDisabled,
    saveSearchLabel = "Save search",
    showNaturalLanguageFilter = false,
    isNaturalLanguageMode = false,
  } = props;

  if (shared) {
    return (
      <MobileSearchFilterBarView
        controller={shared}
        resultView={resultView}
        onResultViewChange={onResultViewChange}
        totalRows={totalRows}
        showResultsCount={showResultsCount}
        onExport={onExport}
        isExporting={isExporting}
        exportDisabled={exportDisabled}
        exportTitle={exportTitle}
        onSaveSearch={onSaveSearch}
        saveSearchDisabled={saveSearchDisabled}
        saveSearchLabel={saveSearchLabel}
        showNaturalLanguageFilter={showNaturalLanguageFilter}
      />
    );
  }

  return (
    <MobileSearchFilterBarStandalone
      {...props}
      isNaturalLanguageMode={isNaturalLanguageMode}
      showNaturalLanguageFilter={showNaturalLanguageFilter}
    />
  );
}

MobileSearchFilterBar.propTypes = {
  resultView: PropTypes.string,
  onResultViewChange: PropTypes.func,
  totalRows: PropTypes.number,
  showResultsCount: PropTypes.bool,
  onExport: PropTypes.func,
  isExporting: PropTypes.bool,
  exportDisabled: PropTypes.bool,
  exportTitle: PropTypes.string,
  onSaveSearch: PropTypes.func,
  saveSearchDisabled: PropTypes.bool,
  saveSearchLabel: PropTypes.string,
  isNaturalLanguageMode: PropTypes.bool,
  showNaturalLanguageFilter: PropTypes.bool,
};
